import { useState, useEffect } from 'react';
import { fetchProperties } from '../utils/api';

const RANGE_FIELDS = [
  { key: '층수', label: '층수' },
  { key: '총층수', label: '총층수' },
  { key: '평수', label: '평수' },
  { key: '보증금', label: '보증금' },
  { key: '월세', label: '월세' },
  { key: '관리비', label: '관리비' },
  { key: '권리비', label: '권리비' },
];

const DEFAULT_SINGLE_OPTIONS = {
  엘베: ['유', '무'],
  화장실: ['내부', '외부']
};

function ConditionFilterModal({ isOpen, onClose, onApply, initialFilters = {} }) {
  const [filters, setFilters] = useState({
    상권: [],
    용도: [],
    엘베: '',
    화장실: '',
    층수: { min: '', max: '' },
    총층수: { min: '', max: '' },
    평수: { min: '', max: '' },
    보증금: { min: '', max: '' },
    월세: { min: '', max: '' },
    관리비: { min: '', max: '' },
    권리비: { min: '', max: '' },
  });
  const [options, setOptions] = useState({
    상권: [],
    용도: [],
    엘베: DEFAULT_SINGLE_OPTIONS.엘베,
    화장실: DEFAULT_SINGLE_OPTIONS.화장실
  });
  const [isLoading, setIsLoading] = useState(false);
  const [customInputs, setCustomInputs] = useState({
    상권: '',
    용도: ''
  });

  useEffect(() => {
    if (isOpen) {
      // 초기 필터 로드
      if (initialFilters && Object.keys(initialFilters).length > 0) {
        setFilters(initialFilters);
      } else {
        setFilters({
          상권: [],
          용도: [],
          엘베: '',
          화장실: '',
          층수: { min: '', max: '' },
          총층수: { min: '', max: '' },
          평수: { min: '', max: '' },
          보증금: { min: '', max: '' },
          월세: { min: '', max: '' },
          관리비: { min: '', max: '' },
          권리비: { min: '', max: '' },
        });
      }
      // 직접 입력 필드 초기화
      setCustomInputs({
        상권: '',
        용도: ''
      });
    }
  }, [isOpen, initialFilters]);

  useEffect(() => {
    async function loadOptions() {
      setIsLoading(true);
      try {
        const data = await fetchProperties();
        const uniqueValues = (key) =>
          Array.from(new Set(data.map((item) => item?.[key]).filter(Boolean)));

        setOptions({
          상권: uniqueValues('상권'),
          용도: uniqueValues('용도'),
          엘베: uniqueValues('엘베').length > 0 ? uniqueValues('엘베') : DEFAULT_SINGLE_OPTIONS.엘베,
          화장실: uniqueValues('화장실').length > 0 ? uniqueValues('화장실') : DEFAULT_SINGLE_OPTIONS.화장실
        });
      } catch (error) {
        console.error('필터 옵션 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    }

    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);

  const handleMultiSelectChange = (key, option, checked) => {
    const currentValues = Array.isArray(filters[key]) ? filters[key] : [];
    if (checked) {
      setFilters(prev => ({ ...prev, [key]: [...currentValues, option] }));
    } else {
      setFilters(prev => ({ ...prev, [key]: currentValues.filter(item => item !== option) }));
    }
  };

  // 직접 입력 추가
  const handleAddCustomValue = (key) => {
    const customValue = customInputs[key]?.trim();
    if (customValue) {
      const currentValues = Array.isArray(filters[key]) ? filters[key] : [];
      if (!currentValues.includes(customValue)) {
        setFilters(prev => ({ ...prev, [key]: [...currentValues, customValue] }));
        setCustomInputs(prev => ({ ...prev, [key]: '' }));
      }
    }
  };

  const handleSingleSelectChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value === '전체' ? '' : value }));
  };

  const handleRangeChange = (key, type, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [type]: value
      }
    }));
  };

  const handleReset = () => {
    setFilters({
      상권: [],
      용도: [],
      엘베: '',
      화장실: '',
      층수: { min: '', max: '' },
      총층수: { min: '', max: '' },
      평수: { min: '', max: '' },
      보증금: { min: '', max: '' },
      월세: { min: '', max: '' },
      관리비: { min: '', max: '' },
      권리비: { min: '', max: '' },
    });
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800">조건 선택</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 다중 선택 필터 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">상권 / 용도</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* 상권 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">상권</label>
                    <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {options.상권.length === 0 ? (
                        <p className="text-xs text-gray-400">선택 가능한 값이 없습니다.</p>
                      ) : (
                        options.상권.map(option => (
                          <label key={option} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters.상권.includes(option)}
                              onChange={(e) => handleMultiSelectChange('상권', option, e.target.checked)}
                              className="text-orange-500 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))
                      )}
                      {/* 직접 입력 */}
                      <div className="pt-2 border-t border-gray-200 mt-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customInputs.상권 || ''}
                            onChange={(e) => setCustomInputs(prev => ({ ...prev, 상권: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCustomValue('상권');
                              }
                            }}
                            placeholder="직접 입력"
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCustomValue('상권')}
                            className="px-3 py-1 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                          >
                            추가
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 용도 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">용도</label>
                    <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                      {options.용도.length === 0 ? (
                        <p className="text-xs text-gray-400">선택 가능한 값이 없습니다.</p>
                      ) : (
                        options.용도.map(option => (
                          <label key={option} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters.용도.includes(option)}
                              onChange={(e) => handleMultiSelectChange('용도', option, e.target.checked)}
                              className="text-orange-500 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))
                      )}
                      {/* 직접 입력 */}
                      <div className="pt-2 border-t border-gray-200 mt-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={customInputs.용도 || ''}
                            onChange={(e) => setCustomInputs(prev => ({ ...prev, 용도: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCustomValue('용도');
                              }
                            }}
                            placeholder="직접 입력"
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddCustomValue('용도')}
                            className="px-3 py-1 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                          >
                            추가
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 단일 선택 필터 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">엘베 / 화장실</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">엘베</label>
                    <select
                      value={filters.엘베 || '전체'}
                      onChange={(e) => handleSingleSelectChange('엘베', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="전체">전체</option>
                      {options.엘베.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">화장실</label>
                    <select
                      value={filters.화장실 || '전체'}
                      onChange={(e) => handleSingleSelectChange('화장실', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="전체">전체</option>
                      {options.화장실.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* 범위 필터 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">범위 조건</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {RANGE_FIELDS.map(field => {
                    const rangeValue = filters[field.key] || { min: '', max: '' };
                    return (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={rangeValue.min || ''}
                            onChange={(e) => handleRangeChange(field.key, 'min', e.target.value)}
                            placeholder="최소"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-500">~</span>
                          <input
                            type="number"
                            value={rangeValue.max || ''}
                            onChange={(e) => handleRangeChange(field.key, 'max', e.target.value)}
                            placeholder="최대"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            초기화
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
            >
              적용
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConditionFilterModal;

